import { Component, ElementRef, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PaymentRequestEndpoint } from '@dato-proxy/endpoints/paymentrequest.endpoint';
import { BaseDocument } from '@dato-proxy/entities/basedocument.entity';
import { DocumentLine } from '@dato-proxy/entities/documentline.entity';
import { PaymentRequest } from '@dato-proxy/entities/paymentrequest.entity';
import { EnumDeliveryMethods, EnumDocumentTypes, EnumPaymentMethod, EnumPaymentRequestStatuses } from '@dato-proxy/enums';
import { ConfirmPaymentRequestParams } from '@dato-proxy/params/confirmpaymentrequest.param';
import { MakePaymentParams } from '@dato-proxy/params/makepayment.param';
import { PaymentRequestResponseParams } from '@dato-proxy/params/paymentrequestresponse.param';
import { PaymentConfigView } from '@dato-proxy/views/paymentconfig.view';
import { BasePageComponent } from '@m4u-angular/base/component/BasePageComponent';
import { ServiceBagService, ServiceBagServices } from '@m4u-angular/base/component/ServiceBag';
import { BaseDataResponse, BaseEntity, BaseEntityResponse, BaseState } from '@m4u-angular/base/entities/BaseEntities';
import { EnumPageDisplayModes } from '@m4u-angular/core/framework/sysenums.enum';
import { Utils } from '@m4u-angular/core/helpers/Utils';
import { PageLayoutComponent } from '@m4u-angular/ui/layouts/layout/page-layout/pagelayout.component';
import { formatNumber } from 'devextreme/localization';
import { MenuItem } from 'primeng/api';
import { Steps } from 'primeng/steps';
import { Globals } from '../../_globals';
import { DateHelpers } from '@core/helpers/DateHelpers';
import { DeviceDetectorService } from 'ngx-device-detector';

enum EnumCreditCardPaymentType {
    Existing = 'Old',
    New = 'New'
}

enum EnumStepType {
    PaymentDetails,
    DocumentDetails,
    Payment,
    Final
}

class State extends BaseState {
    Steps: MenuItem[] = [];
    Step: number;
    StepType: EnumStepType;
    PaymentMethod: EnumPaymentMethod;
    PaymentTotal: number;
    IsPartialPayment: boolean;
    NumberOfPayments: number;
    ReceiptEmail: string;
    DeliveryMethods = [];
    DeliveryMethod: EnumDeliveryMethods;
    CreditCardPaymentType: EnumCreditCardPaymentType;
    IsSaveCardNumberAsDefault: boolean;
    IsAgreeWithTermsOfUse: boolean;

    Documents: BaseDocument[] = Globals.PaymentDocuments;
    DocumentItems: DocumentLine[] = [];
    SelectedDocumentItems: DocumentLine[] = [];
    Options = Globals.SystemOption;

    VisibleLoadPanel: boolean;
    Currency: string;
    CustomerCardNumber: string;
    IsAllowPartialPayment: boolean;
    NewCreditCardUrl: SafeResourceUrl;
    IsSucceedPayment: boolean;
    IsLargeIframe: boolean;
    ErrorMessage: string;

    PaymentRequest: PaymentRequest;
}

@Component({
    providers: ServiceBagServices,
    selector: 'app-payment-dialog',
    templateUrl: './payment-dialog.component.html',
    styleUrls: ['./payment-dialog.component.scss']
})
export class PaymentDialogComponent extends BasePageComponent<BaseEntity, State> {

    EnumPaymentMethod = EnumPaymentMethod;
    EnumStepType = EnumStepType;
    EnumCreditCardPaymentType = EnumCreditCardPaymentType;

    CreditCardPaymentTypes = [...this.LIST('ChoiseCardByPayment')];

    get Steps(): MenuItem[] {
        const steps = [
            { id: EnumStepType.PaymentDetails.toString(), label: this.RC('titlePaymentDetails') },
            { id: EnumStepType.Payment.toString(), label: this.RC('titlePayment') }
        ];
        if (this.State.IsPartialPayment) {
            steps.splice(1, 0, { id: EnumStepType.DocumentDetails.toString(), label: this.RC('titleDocumentDetails') });
        }
        return steps;
    }

    get DeliveryMethods() {
        const deliveryMethods = [];
        if (this.State.Options.InvoiceOneIsAllowEmail) deliveryMethods.push({...this.LISTITEMX('DeliveryMethods', EnumDeliveryMethods.Email)});
        if (this.State.Options.InvoiceOneIsAllowSMS) deliveryMethods.push({...this.LISTITEMX('DeliveryMethods', EnumDeliveryMethods.Phone)});
        if (this.State.Options.InvoiceOneIsAllowWhatsapp) deliveryMethods.push({...this.LISTITEMX('DeliveryMethods', EnumDeliveryMethods.WhatsApp)});
        return deliveryMethods;
    }

    PaymentNumbers = Array.from(Array(this.State.Options.MaxNumberOfPayments).keys())
        .map(x => { return { Text: formatNumber(x + 1, this.F.NumberQuantity), Value: x + 1 } });

    @ViewChild('steps') steps: Steps;
    @ViewChild('page') pageLayout: PageLayoutComponent;
    @ViewChild('iframe') iframe: ElementRef;

    constructor(A: ServiceBagService,
        private paymentRequestEndpoint: PaymentRequestEndpoint,
        private sanitizer: DomSanitizer,
        private deviceDetector: DeviceDetectorService) {
        super(A, 'PaymentDialogComponent', BaseEntity, State, EnumPageDisplayModes.Dialog);
    }

    protected override OnInitComponent(): void {
        if (!this.State.Options.IsAllowPayment) {
            this.UI.StopMessage('Payment is not allowed');
            this.CloseDialogWithCancel();
            return;
        }
        
        this.State.IsLargeIframe = false;
        this.State.Step = 0;

        this.State.PaymentMethod = EnumPaymentMethod.CreditCard;
        this.State.NumberOfPayments = 1;
        this.State.IsSaveCardNumberAsDefault = true;
        this.State.Documents = Globals.PaymentDocuments;
        this.State.PaymentTotal = this.State.Documents.reduce((sum, current) => sum + current.PaymentSum, 0);

        const oFirstDocument =  this.State.Documents[0];
        this.State.Currency = oFirstDocument.PaymentCurrency;
        this.State.VisibleLoadPanel = true;

        this.InitSteps();
        this.InitDeliveryMethods();
        
        this.Do(this.paymentRequestEndpoint.GetPaymentConfig(oFirstDocument.DocumentNumber, oFirstDocument.TransType), false, (response: BaseEntityResponse<PaymentConfigView>) => {
            this.State.CustomerCardNumber = response.Entity.CustomerCardNumber;

            this.CreditCardPaymentTypes = Utils.CloneObject(this.LIST('ChoiseCardByPayment'));
            this.CreditCardPaymentTypes.forEach(x => x.Text = x.Text.replace('{0}', this.State.CustomerCardNumber));

            this.State.ReceiptEmail = response.Entity.Email;
            this.State.DeliveryMethod = !response.Entity.DeliveryMethod || response.Entity.DeliveryMethod === EnumDeliveryMethods.Unknown 
                ? this.State.DeliveryMethods[0]?.Value : response.Entity.DeliveryMethod;
            this.State.DocumentItems = response.Entity.DocumentLines;
            this.State.IsAllowPartialPayment = this.State.Options.IsAllowPaymentByDocumentItems && this.State.DocumentItems?.length && this.State.Documents.length < 2;
            this.State.CreditCardPaymentType = this.State.Options.IsAllowPaymentByExistsCreditCard && !Utils.IsEmpty(this.State.CustomerCardNumber) ? EnumCreditCardPaymentType.Existing : EnumCreditCardPaymentType.New
            this.State.VisibleLoadPanel = false;
            this.NotifyReloadTitle();
            setTimeout(() => this.A.Screen.IsMobile() && this.pageLayout?.popup?.instance?.repaint());
        }, (response: BaseEntityResponse<PaymentConfigView>) => {
            this.State.VisibleLoadPanel = false;
            this.A.UI.StopMessage(response.ResponseMessage);
            this.CloseDialogWithCancel();
        });

        // Init reload actions
        (window.document as any).OnSuccessfulPayment = (privateRequestID: string) => { this.ConfirmPaymentRequest(privateRequestID); };
        (window.document as any).OnBackPayment = () => { this.CloseDialogWithCancel(); };

        window.onmessage = (event: any) => {
            if (event.data.type && event.data.type == "OnHeightChanged") {
                this.ResizeIframe(event.data.height);
            }
        };
    }

    protected override OnInitComponentFinished(): void {
        this.steps?.activeIndexChange.emit(this.State.Step);
        this.Repaint(true);
    }

    override OnInitTitle() {
        if (this.State.Documents?.length) {
            if (this.State.Documents.length === 1) {
                return `${this.RC('titlePaymentByOneDoc')} ${this.State.Documents[0].DocumentNumber}`;
            } else if (this.State.Documents.length > 1) {
                return this.RC('titlePaymentByManyDocs')?.replace('{n}', this.State.Documents.length.toString());
            }
        }

        return this.RC('titlePayment');
    }

    override OnIsDirtyDisabled(): boolean {
        return true;
    }

    private ConfirmPaymentRequest(privateRequestID: string) {
        // Show loading panel
        this.State.VisibleLoadPanel = true;

        // Update params
        const params = new ConfirmPaymentRequestParams();
        params.IsSaveCardNumberAsDefault = this.State.IsSaveCardNumberAsDefault;
        params.PrivateID = privateRequestID;

        // Excecute confirm
        this.Do(this.paymentRequestEndpoint.Confirm(params), false, (response: PaymentRequestResponseParams) => {
            this.State.CustomerCardNumber = response.CreditCardNumber;
            this.ShowPaymentResult(true);
        }, () => {
            this.ShowPaymentResult(false);
        });
    }

    private ShowPaymentResult(isSuccess: boolean, response?: PaymentRequestResponseParams, errorMessage?: string) {
        this.State.IsSucceedPayment = isSuccess;
        this.State.VisibleLoadPanel = false;
        this.State.ErrorMessage = errorMessage;

        if (isSuccess) this.NotifyEntitySaved(undefined, PaymentRequest);
        else {
            if (response && response.IsGoCreditError) {
                this.A.UI.StopMessage(response.ResponseMessage);
            }
        }
        
        this.State.Step = this.Steps.length;
        this.steps.activeIndexChange.emit(this.State.Step);
    }

    IsValidTotalSum = () => {
        return this.State.PaymentTotal > 0;
    }

    IsValidReceiptTarget = () => {
        return this.State.DeliveryMethod === EnumDeliveryMethods.Email
            ? (Utils.IsEmpty(this.State.ReceiptEmail) || Utils.IsEmail(this.State.ReceiptEmail, true))
            : (Utils.IsEmpty(this.State.ReceiptEmail) || !Utils.IsEmail(this.State.ReceiptEmail, true));
    }

    GetReceiptTargetValidationMessage() {
        return this.State.DeliveryMethod === EnumDeliveryMethods.Email
            ? this.RC('msgValidationEmailNotValid')
            : this.RC('msgValidationReceiptTargetNotValid')
    }

    OnDeliveryMethodChanged(e) {
        // this is for validation rule reevaluation
        const receiptEmail = this.State.ReceiptEmail;
        this.State.ReceiptEmail = '';
        setTimeout(() => this.State.ReceiptEmail = receiptEmail);
    }

    OnPaymentMethodSelected(type: EnumPaymentMethod) {
        this.State.PaymentMethod = type;
        this.State.NumberOfPayments = 1;
    }

    OKText() {
        return this.State.StepType === EnumStepType.Payment ? this.RC('linkDocumentPayNow') : 
            this.State.StepType === EnumStepType.Final ? this.RC('lblFinish') : this.RC('lblNext');
    }

    OnOK() {
        if (this.State.StepType === EnumStepType.Final) {
            this.State.IsSucceedPayment ? this.CloseDialogWithSuccess() : this.CloseDialogWithCancel();
            this.BottomSheetContext && this.BottomSheetContext.dismiss();
            return;
        }
        if (this.State.StepType === EnumStepType.Payment) {
            this.OnPay();
        }
        
        this.State.Step++;
        this.steps.activeIndexChange.emit(this.State.Step);
    }

    CancelText() {
        return this.State.StepType === EnumStepType.PaymentDetails ? this.RC('genCancel') : this.RC('lblBack');
    }

    OnCancel() {
        if (this.State.StepType === EnumStepType.PaymentDetails) {
            this.CloseDialogWithCancel();
            this.BottomSheetContext && this.BottomSheetContext.dismiss();
        } else {
            this.State.Step--;
            this.steps.activeIndexChange.emit(this.State.Step);
        }
    }

    private OnPay() {
        if (this.State.PaymentMethod === EnumPaymentMethod.CreditCard) {
            this.State.VisibleLoadPanel = true;
            this.Do(this.paymentRequestEndpoint.Execute(this.GetMakePaymentParams()), false, (response) => {
                this.State.CustomerCardNumber = response.CreditCardNumber;
                this.ShowPaymentResult(true, response)
            }, (response) => this.ShowPaymentResult(false, response));
            return;
        }
        this.SendPaymentRequest((response) => {
            const externalPaymentUrl = response.Entity.Url;
            window.location.href = externalPaymentUrl;
        });
    }

    private ShowNewCardDetails() {
        if (this.State.PaymentMethod !== EnumPaymentMethod.CreditCard) return;

        this.SendPaymentRequest((response) => {
            this.State.NewCreditCardUrl = this.sanitizer.bypassSecurityTrustResourceUrl(response.Entity.Url);
            this.Repaint();
        });
    }

    private SendPaymentRequest(callback: (response: BaseEntityResponse<PaymentRequest>) => void) {
        this.State.VisibleLoadPanel = true;

        this.Do(this.paymentRequestEndpoint.Send(this.GetMakePaymentParams()), false, (response: BaseEntityResponse<PaymentRequest>) => {
            this.State.VisibleLoadPanel = false;
            callback(response);
        }, (response) => {
            this.State.VisibleLoadPanel = false;
            this.ShowPaymentResult(false);
            this.UI.StopMessage(response.ResponseMessage);
        });
    }

    private GetMakePaymentParams() {
        const params = new MakePaymentParams();
        params.Sum = this.State.PaymentTotal;
        params.Currency = this.State.Currency;
        params.NumberOfPayments = this.State.NumberOfPayments;
        params.DocumentIDs = this.State.Documents.map(x => x.EntryID);
        params.Email = this.State.ReceiptEmail;
        params.DeliveryMethod = this.State.DeliveryMethod;
        params.PaymentMethod = this.State.PaymentMethod;
        params.SessionID = this.A.Session.SessionID;

        if (this.State.Options.IsEnableMultiCompany) { params.CompanyID = this.State.Documents[0].CompanyID; }
        if (this.State.Options.IsAllowPaymentByDocumentItems && this.State.SelectedDocumentItems) { params.DocumentLineIDs = this.State.SelectedDocumentItems.map(x => (x as any).LineID); }

        const firstDocument = this.State.Documents[0];
        params.DocumentsType = this.GetDocumentType(firstDocument.TransType);

        return params;
    }

    private GetDocumentType(transType: number) {
        switch (transType) {
            case 15: return EnumDocumentTypes.Delivery;
            case 17: return EnumDocumentTypes.Order;
            case 23: return EnumDocumentTypes.PriceProposal;
            default: EnumDocumentTypes.LedgerTypes;
        }
    }

    OnCreditCardPaymentTypeChanged() {
        if (this.State.CreditCardPaymentType === EnumCreditCardPaymentType.New) {
            this.ShowNewCardDetails();
        } else {
            this.State.NewCreditCardUrl = null;
        }
    }

    OnSelectedLineIDsChange(selectedLines: DocumentLine[]) {
        this.State.PaymentTotal = selectedLines.reduce((sum, current) => sum + current.TotalSum, 0);
        this.State.SelectedDocumentItems = selectedLines;
    }

    OnStepChanged(e) {
        if (Utils.IsEmpty(e) || !this.Steps || !this.Steps.length) return;

        this.State.StepType = e === this.Steps.length ? EnumStepType.Final : +this.Steps[e].id as EnumStepType;
        if (this.State.StepType === EnumStepType.Payment) {
            if (this.State.PaymentMethod === EnumPaymentMethod.CreditCard && this.State.CreditCardPaymentType === EnumCreditCardPaymentType.New) {
                this.ShowNewCardDetails();
                return;
            }
            if (this.State.PaymentMethod === EnumPaymentMethod.Paypal || this.State.PaymentMethod === EnumPaymentMethod.Bit) {
                this.OnPay();
                return;
            }
        }
        this.Repaint(true);
    }

    GetPaymentMethod() {
        switch (this.State.PaymentMethod) {
            case EnumPaymentMethod.CreditCard: return this.State.CustomerCardNumber ? `****${this.State.CustomerCardNumber}` : this.RC('lblCreditCard');
            case EnumPaymentMethod.Paypal: return this.RC('lblPaypal');
            case EnumPaymentMethod.Bit: return this.RC('lblBit');
        }
    }

    Repaint(isAutoHeight?: boolean) {
        const popupInstance = this.pageLayout?.popup?.instance as any;
        if (!popupInstance) return;

        if (isAutoHeight) popupInstance.option('height', 'auto');
        setTimeout(() => popupInstance.repaint());
    }

    ResizeIframe = (iframeHeight: number) => {
        if (iframeHeight == null || iframeHeight == 0) return;
        
        this.State.VisibleLoadPanel = true;

        const iframeHeightPx = `${iframeHeight}px`;

        const popupInstance = this.pageLayout?.popup?.instance as any;
        if (popupInstance) {
            this.iframe.nativeElement.style.height = iframeHeightPx;

            const iframeRect = document.querySelector(`.${this.pageLayout.Class} iframe`).getBoundingClientRect();
            const iframeTop = iframeRect.top;

            const popupTop = popupInstance.content().getBoundingClientRect().top;
            const popupPadding = +window.getComputedStyle(document.querySelector(`.${this.pageLayout.Class} .${popupInstance.content().classList[0]} .page-body`))
                .padding.replace('px', '');
    
            const calculatedPopupHeight = iframeTop - popupTop + iframeHeight + popupPadding;
            const maxHeight = window.innerHeight - 20; // some indent to make it looks like a window
            this.State.IsLargeIframe = calculatedPopupHeight > maxHeight;
    
            const newPopupHeight = this.State.IsLargeIframe ? maxHeight : calculatedPopupHeight;
            popupInstance.option('height', newPopupHeight);
            this.Repaint();
        } else {
            const iframes = document.querySelectorAll('.sheet-content iframe');
            const iframe = iframes.length && iframes[iframes.length - 1] as any;

            iframe && (iframe.style.height = iframeHeightPx);
        }

        this.State.VisibleLoadPanel = false;
    }

    InitSteps() {
        this.State.Steps = this.Steps;
    }

    InitDeliveryMethods() {
        this.State.DeliveryMethods = this.DeliveryMethods;
    }

    OnIsPartialPaymentChanged() {
        this.InitSteps();
    }

    IsDeliveryMethodVisible() {
        return [this.State.Options.InvoiceOneIsAllowEmail, this.State.Options.InvoiceOneIsAllowSMS, this.State.Options.InvoiceOneIsAllowWhatsapp]
            .reduce((sum, current) => sum += +current, 0) > 1;
    }

    IsBitMethodVisible() {
        return this.State.Options.IsAllowBitPayment && (this.deviceDetector.os === 'Android' || this.deviceDetector.os === 'iOS');
    }
}
